import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Tag,
  Alert,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import medicineService, { Medicine } from "../../services/medicineService";
import { packDefaultsForCategory } from "../../utils/pack";
import PageHeader from "../../components/PageHeader";

const { Option } = Select;

const Medicines: React.FC = () => {
  const [items, setItems] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState<Medicine | null>(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [form] = Form.useForm();

  const load = async (page = 1, searchTerm = search) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await medicineService.getAll({
        page,
        limit: pagination.pageSize,
        search: searchTerm || undefined,
      });
      setItems(res.data.data);
      setPagination((p) => ({
        ...p,
        current: res.data.pagination.currentPage,
        total: res.data.pagination.totalItems,
      }));
    } catch (e: any) {
      setLoadError(e.response?.data?.message || "Failed to load medicines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      category: "tablet",
      unit: "pieces",
      purchaseUnit: "strips",
      schedule: "otc",
      defaultGstRate: 12,
      packSize: 10,
      reorderLevel: 100,
    });
    setVisible(true);
  };

  const openEdit = (record: Medicine) => {
    setEditing(record);
    form.setFieldsValue(record);
    setVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await medicineService.update(editing._id, values);
      } else {
        await medicineService.create(values);
      }
      setVisible(false);
      load(pagination.current);
    } catch (e: any) {
      // API errors toasted by interceptor; form field errors stay inline
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await medicineService.remove(id);
      load(pagination.current);
    } catch {
      // API errors toasted by interceptor
    }
  };

  const columns = [
    { title: "Code", dataIndex: "code", key: "code", width: 100 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Generic", dataIndex: "genericName", key: "genericName" },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (c: string) => <Tag>{c}</Tag>,
    },
    { title: "HSN", dataIndex: "hsnCode", key: "hsnCode", width: 100 },
    {
      title: "Pack",
      key: "pack",
      width: 140,
      render: (_: any, r: Medicine) =>
        `${r.packSize || 1} ${r.unit}/${r.purchaseUnit || r.unit}`,
    },
    {
      title: "GST %",
      dataIndex: "defaultGstRate",
      key: "defaultGstRate",
      width: 80,
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
      width: 90,
      render: (s: string) => s?.toUpperCase(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: Medicine) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Deactivate this medicine?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Medicines (Product Master)"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Medicine
          </Button>
        }
      />
      <div className="page-panel">
        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={
              <Button size="small" onClick={() => load(pagination.current)}>
                Retry
              </Button>
            }
            style={{ marginBottom: 10 }}
          />
        )}
        <Space style={{ marginBottom: 10 }}>
          <Input.Search
            placeholder="Search code, name, HSN..."
            allowClear
            onSearch={(v) => {
              setSearch(v);
              load(1, v);
            }}
            style={{ width: 320 }}
          />
        </Space>
        <Table
          className="dense-table"
          size="small"
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            ...pagination,
            onChange: (page) => load(page),
          }}
        />
      </div>

      <Modal
        title={editing ? "Edit Medicine" : "Add Medicine"}
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Space style={{ display: "flex" }} size={12}>
            <Form.Item
              name="code"
              label="Code / SKU"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Input disabled={!!editing} />
            </Form.Item>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true }]}
              style={{ flex: 2 }}
            >
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ display: "flex" }} size={12}>
            <Form.Item name="genericName" label="Generic Name" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="manufacturer"
              label="Manufacturer"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ display: "flex" }} size={12} wrap>
            <Form.Item name="category" label="Category" rules={[{ required: true }]}>
              <Select
                style={{ width: 140 }}
                showSearch
                optionFilterProp="children"
                onChange={(cat) => {
                  form.setFieldsValue(packDefaultsForCategory(cat));
                }}
              >
                {["tablet", "capsule", "syrup", "injection", "cream", "drops", "inhaler", "other"].map(
                  (c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
            <Form.Item
              name="unit"
              label="Stock / Sale Unit"
              rules={[{ required: true }]}
              tooltip="Inventory and billing quantity (e.g. pieces/tablets)"
            >
              <Select style={{ width: 140 }} showSearch>
                {["pieces", "bottles", "boxes", "strips", "vials"].map((u) => (
                  <Option key={u} value={u}>
                    {u}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="purchaseUnit"
              label="Purchase Unit"
              rules={[{ required: true }]}
              tooltip="Unit on supplier invoices (e.g. strips)"
            >
              <Select style={{ width: 140 }} showSearch>
                {["pieces", "bottles", "boxes", "strips", "vials"].map((u) => (
                  <Option key={u} value={u}>
                    {u}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="packSize"
              label="Pack Size"
              rules={[{ required: true }]}
              tooltip="How many stock units in 1 purchase unit (e.g. 10 tablets per strip)"
            >
              <InputNumber min={1} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="hsnCode" label="HSN Code">
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="defaultGstRate" label="GST %">
              <InputNumber min={0} max={100} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="schedule" label="Schedule">
              <Select style={{ width: 100 }}>
                <Option value="otc">OTC</Option>
                <Option value="h">H</Option>
                <Option value="h1">H1</Option>
                <Option value="x">X</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="reorderLevel"
              label="Reorder (stock units)"
              tooltip="In stock/sale units (tablets), not strips"
            >
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="defaultMrp"
              label="Default MRP / pack"
              tooltip="MRP per purchase unit (per strip) as on pack"
            >
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="defaultSellingPrice"
              label="Sale Price / pack"
              tooltip="Sale price per purchase unit; converted to per tablet on stock"
            >
              <InputNumber min={0} style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="barcode" label="Barcode">
              <Input style={{ width: 140 }} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Medicines;
